import React, { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Calendar,
  Users,
  LogOut,
  ClipboardList,
  BarChart3,
  CreditCard,
  History,
  Menu,
  Scissors
} from 'lucide-react';
import './AdminLayout.css';

/**
 * 관리자 페이지 공통 레이아웃
 * - 사이드바와 헤더를 포함
 * - 모든 관리자 페이지에 적용되는 표준 레이아웃
 */
function AdminLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const adminUser = localStorage.getItem('adminUser') || '관리자';

  const handleLogout = () => {
    if (window.confirm('로그아웃 하시겠습니까?')) {
      localStorage.removeItem('isLoggedIn');
      localStorage.removeItem('adminUser');
      localStorage.removeItem('rememberMe');
      localStorage.removeItem('token');
      localStorage.removeItem('username');
      localStorage.removeItem('role');
      navigate('/login');
    }
  };

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const getTodayDate = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    const days = ['일', '월', '화', '수', '목', '금', '토'];
    const dayOfWeek = days[today.getDay()];
    
    return `${year}년 ${month}월 ${day}일 (${dayOfWeek})`;
  };

  // 현재 경로에 따라 페이지 타이틀 결정
  const getPageTitle = () => {
    const path = location.pathname;
    if (path === '/admin') return '대시보드';
    if (path === '/admin/reservations') return '예약 관리';
    if (path === '/admin/customers') return '고객 관리';
    if (path.startsWith('/admin/customers/')) return '고객 상세 정보';
    if (path === '/admin/care-reports') return '케어 리포트';
    if (path === '/admin/treatments') return '시술 관리';
    if (path === '/admin/packages') return '패키지 관리';
    if (path === '/admin/packages/history') return '패키지 이력 관리';
    if (path === '/admin/dashboard') return '매출 통계';
    return '관리자';
  };

  // 네비게이션 메뉴가 활성화되었는지 확인
  const isActive = (path) => {
    return location.pathname === path;
  };

  return (
    <div className="admin-page">
      {/* Sidebar */}
      <aside className={`admin-sidebar ${isSidebarOpen ? 'open' : 'closed'} ${isMobileMenuOpen ? 'mobile-menu-active' : ''}`}>
        <div className="sidebar-header">
          <h2 className="sidebar-logo">ANEW</h2>
          <button className="sidebar-toggle" onClick={toggleSidebar}>
            {isSidebarOpen ? '◀' : '▶'}
          </button>
        </div>

        <nav className="sidebar-nav">
            <div className="nav-section">
              <button 
                className={`nav-item ${isActive('/admin') ? 'active' : ''}`}
                onClick={() => navigate('/admin')}
              >
                <LayoutDashboard className="nav-icon-lucide" size={20} />
                <span className="nav-text">대시보드</span>
              </button>

              <button 
                className={`nav-item ${isActive('/admin/reservations') ? 'active' : ''}`}
                onClick={() => navigate('/admin/reservations')}
              >
                <Calendar className="nav-icon-lucide" size={20} />
                <span className="nav-text">예약 관리</span>
              </button>

              <button 
                className={`nav-item ${isActive('/admin/customers') ? 'active' : ''}`}
                onClick={() => navigate('/admin/customers')}
              >
                <Users className="nav-icon-lucide" size={20} />
                <span className="nav-text">고객 관리</span>
              </button>

              <button
                className={`nav-item ${isActive('/admin/care-reports') ? 'active' : ''}`}
                onClick={() => navigate('/admin/care-reports')}
              >
                <ClipboardList className="nav-icon-lucide" size={20} />
                <span className="nav-text">케어 리포트</span>
              </button>

              <button
                className={`nav-item ${isActive('/admin/treatments') ? 'active' : ''}`}
                onClick={() => navigate('/admin/treatments')}
              >
                <Scissors className="nav-icon-lucide" size={20} />
                <span className="nav-text">시술 관리</span>
              </button>

              <button
                className={`nav-item ${isActive('/admin/packages') ? 'active' : ''}`}
                onClick={() => navigate('/admin/packages')}
              >
                <CreditCard className="nav-icon-lucide" size={20} />
                <span className="nav-text">패키지 관리</span>
              </button>

              <button
                className={`nav-item ${isActive('/admin/packages/history') ? 'active' : ''}`}
                onClick={() => navigate('/admin/packages/history')}
              >
                <History className="nav-icon-lucide" size={20} />
                <span className="nav-text">패키지 이력</span>
              </button>

              <button
                className={`nav-item ${isActive('/admin/dashboard') ? 'active' : ''}`}
                onClick={() => navigate('/admin/dashboard')}
              >
                <BarChart3 className="nav-icon-lucide" size={20} />
                <span className="nav-text">매출 통계</span>
              </button>
            </div>

            <div className="nav-section">
              <button 
                className="nav-item logout"
                onClick={handleLogout}
              >
                <LogOut className="nav-icon-lucide" size={20} />
                <span className="nav-text">로그아웃</span>
              </button>
            </div>
          </nav>
      </aside>

      {/* Main Content */}
      <main className="admin-content">
        {/* Header */}
        <header className="admin-header">
          <div className="header-left">
            {/* Mobile menu toggle - only visible on mobile */}
            <button
              className="mobile-menu-toggle"
              onClick={toggleMobileMenu}
              aria-label="메뉴 열기"
            >
              <Menu size={24} />
            </button>
            <div>
              <h1 className="page-title">{getPageTitle()}</h1>
              <p className="page-subtitle">{getTodayDate()}</p>
            </div>
          </div>
          <div className="header-right">
            <div className="user-info">
              <span className="user-icon">👤</span>
              <span className="user-name">{adminUser}님</span>
            </div>
          </div>
        </header>

        {/* 페이지별 컨텐츠 영역 (React Router Outlet) */}
        <Outlet />
      </main>

      {/* Mobile sidebar overlay */}
      <div
        className={`sidebar-overlay ${isMobileMenuOpen ? 'active' : ''}`}
        onClick={() => setIsMobileMenuOpen(false)}
      />
    </div>
  );
}

export default AdminLayout;
